


import {
  Check,
  AutoAwesome as Sparkles
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { StructureTemplate } from '@/lib/types';

import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useResponsiveValue } from '@/hooks/use-responsive-value';
import { useToast } from '@/hooks/use-toast';

// Built-in narrative structure templates
const BUILT_IN_TEMPLATES: StructureTemplate[] = [
  {
    id: 'heros-journey',
    name: "Hero's Journey",
    description: "Joseph Campbell's monomyth - a universal story pattern of transformation",
    isBuiltIn: true,
    layoutType: 'circular' as const,
    defaultSections: [
      { id: 'ordinary-world', name: 'Ordinary World', order: 0, color: '#64748b' },
      { id: 'call-to-adventure', name: 'Call to Adventure', order: 1, color: '#3b82f6' },
      { id: 'refusal', name: 'Refusal of the Call', order: 2, color: '#ef4444' },
      { id: 'mentor', name: 'Meeting the Mentor', order: 3, color: '#10b981' },
      { id: 'threshold', name: 'Crossing the Threshold', order: 4, color: '#f59e0b' },
      { id: 'tests', name: 'Tests, Allies, Enemies', order: 5, color: '#8b5cf6' },
      { id: 'innermost-cave', name: 'Approach to Inmost Cave', order: 6, color: '#06b6d4' },
      { id: 'ordeal', name: 'Ordeal', order: 7, color: '#dc2626' },
      { id: 'reward', name: 'Reward', order: 8, color: '#eab308' },
      { id: 'road-back', name: 'The Road Back', order: 9, color: '#84cc16' },
      { id: 'resurrection', name: 'Resurrection', order: 10, color: '#a855f7' },
      { id: 'return-elixir', name: 'Return with the Elixir', order: 11, color: '#14b8a6' },
    ],
  },
  {
    id: 'kishotenketsu',
    name: 'Kishōtenketsu',
    description: 'Four-act structure from East Asian narrative tradition - no conflict required',
    isBuiltIn: true,
    layoutType: 'linear' as const,
    defaultSections: [
      { id: 'ki', name: 'Ki (起) - Introduction', order: 0, color: '#10b981', description: 'Establish setting and characters' },
      { id: 'sho', name: 'Shō (承) - Development', order: 1, color: '#3b82f6', description: 'Develop the story without major changes' },
      { id: 'ten', name: 'Ten (転) - Twist', order: 2, color: '#f59e0b', description: 'Unexpected turn or new perspective' },
      { id: 'ketsu', name: 'Ketsu (結) - Conclusion', order: 3, color: '#8b5cf6', description: 'Reconcile and conclude' },
    ],
  },
  {
    id: 'fichtean-curve',
    name: 'Fichtean Curve',
    description: 'Begin in medias res with escalating crises building to climax',
    isBuiltIn: true,
    layoutType: 'curve' as const,
    defaultSections: [
      { id: 'inciting-crisis', name: 'Inciting Crisis', order: 0, color: '#ef4444' },
      { id: 'rising-crisis-1', name: 'Rising Crisis 1', order: 1, color: '#f97316' },
      { id: 'rising-crisis-2', name: 'Rising Crisis 2', order: 2, color: '#eab308' },
      { id: 'rising-crisis-3', name: 'Rising Crisis 3', order: 3, color: '#84cc16' },
      { id: 'climax', name: 'Climax', order: 4, color: '#dc2626' },
      { id: 'falling-action', name: 'Falling Action', order: 5, color: '#8b5cf6' },
    ],
  },
  {
    id: 'three-act',
    name: 'Three-Act Structure',
    description: 'Classic dramatic structure: Setup, Confrontation, Resolution',
    isBuiltIn: true,
    layoutType: 'linear' as const,
    defaultSections: [
      { id: 'act-1-setup', name: 'Act I - Setup', order: 0, color: '#3b82f6', description: 'Introduce world, characters, conflict' },
      { id: 'act-2-confrontation', name: 'Act II - Confrontation', order: 1, color: '#f59e0b', description: 'Rising action, obstacles, midpoint' },
      { id: 'act-3-resolution', name: 'Act III - Resolution', order: 2, color: '#10b981', description: 'Climax and denouement' },
    ],
  },
  {
    id: 'custom',
    name: 'Custom Structure',
    description: 'Start with a blank slate and define your own sections',
    isBuiltIn: true,
    layoutType: 'grid' as const,
    defaultSections: [
      { id: 'section-1', name: 'Section 1', order: 0, color: '#64748b' },
    ],
  },
];


export function StructureSelectionGate() {
  const { t } = useTranslation();
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const { toast } = useToast();
  const resolveResponsive = useResponsiveValue();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isApplying, setIsApplying] = useState(false);

  // Helper to get translated template info
  const getTemplateInfo = (id: string, field: 'name' | 'description', fallback: string) => {
    const key = `narrative.templates.${id.replace(/-/g, '_')}.${field}`;
    const translated = t(key);
    return translated === key ? fallback : translated;
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplateId || !novel.id) return;

    const template = BUILT_IN_TEMPLATES.find(t => t.id === selectedTemplateId);
    if (!template) return;

    setIsApplying(true);
    try {
      const templateSections = template.defaultSections.map(section => ({
        id: section.id,
        templateId: template.id,
        name: section.name,
        order: section.order,
        color: section.color || '#64748b',
        description: section.description || '',
      }));

      await backend.narrative.applyStructureTemplate(
        novel.ownerId,
        novel,
        template.id,
        templateSections,
        []
      );

      toast({
        title: t('narrative.selection.success_title'),
        description: t('narrative.selection.success_desc', { template: getTemplateInfo(template.id, 'name', template.name) }),
      });
    } catch (error) {
      console.error('Apply template error:', error);
      toast({
        title: t('entity_form.scenes.error_title'),
        description: error instanceof Error ? error.message : t('narrative.selection.apply_failed_desc'),
        variant: 'destructive',
      });
    } finally {
      setIsApplying(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '80vh',
        p: 4,
        textAlign: 'center',
      }}
    >
      <Sparkles sx={{ fontSize: 48, mb: 2, opacity: 0.7 }} />
      <Typography variant="h4" gutterBottom fontWeight={700}>
        {t('narrative.selection.title')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600 }}>
        {t('narrative.selection.description')}
      </Typography>

      <Grid container spacing={3} sx={{ maxWidth: 1000, mb: 4 }}>
        {BUILT_IN_TEMPLATES.map((template) => (
          <Grid key={template.id} size={resolveResponsive({ xs: 12, sm: 6, md: 4 })}>
            <Card
              sx={{
                height: '100%',
                border: 2,
                borderColor: selectedTemplateId === template.id ? 'primary.main' : 'divider',
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: 'primary.light',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <CardActionArea
                onClick={() => setSelectedTemplateId(template.id)}
                sx={{ height: '100%', p: 2 }}
              >
                <CardContent sx={{ p: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" fontWeight={600}>
                      {getTemplateInfo(template.id, 'name', template.name)}
                    </Typography>
                    {selectedTemplateId === template.id && (
                      <Check sx={{ fontSize: 20, color: '#10b981' }} />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {getTemplateInfo(template.id, 'description', template.description || '')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {template.defaultSections.slice(0, 4).map((section) => (
                      <Chip
                        key={section.id}
                        label={section.name === 'Unassigned' ? t('narrative.selection.unassigned_name') : section.name}
                        size="small"
                        sx={{
                          bgcolor: section.color + '20',
                          color: section.color,
                          fontSize: '0.7rem',
                        }}
                      />
                    ))}
                    {template.defaultSections.length > 4 && (
                      <Chip
                        label={t('narrative.selection.more', { count: template.defaultSections.length - 4 })}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    )}
                    {template.defaultSections.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {t('narrative.selection.define_own')}
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Button
        variant="contained"
        size="large"
        disabled={!selectedTemplateId || isApplying}
        onClick={handleApplyTemplate}
        startIcon={isApplying ? <CircularProgress size={20} /> : null}
        sx={{ px: 6, py: 1.5 }}
      >
        {isApplying ? t('narrative.selection.applying') : t('narrative.selection.apply')}
      </Button>
    </Box>
  );
}
